/**
 * Состояние замка: заперто или открыто.
 *
 * Живёт в памяти и умирает вместе с вкладкой — это и есть главное свойство.
 * Хранить «уже открыто» в базе значило бы, что замок переживает перезапуск и
 * не запирается никогда.
 *
 * ## Когда запирается
 *
 * При запуске — всегда, если замок включён. И при возвращении из фона, если
 * человека не было дольше выбранной задержки. Считаем время именно в фоне, а
 * не «время бездействия»: приложение не следит за тем, что человек делает с
 * открытым экраном, и не должно.
 */

import { create } from 'zustand';

import { readLockSettings, readPinDigest } from './lock-settings';
import { verifyPin } from './pin';

interface LockState {
  /** `null` — ещё не выяснили, включён ли замок. Экран ждёт, а не мигает. */
  locked: boolean | null;
  /** Сколько раз подряд ввели неверный код. */
  attempts: number;
  /** Когда приложение ушло в фон. */
  awaySince: number | null;

  /** Выясняет состояние замка при запуске приложения. */
  init: (userId: string) => Promise<void>;
  unlock: (userId: string, pin: string) => Promise<boolean>;
  /** Открыть без кода — по отпечатку или после проверки пароля аккаунта. */
  open: () => void;
  lock: () => void;
  /** Приложение свернули или развернули. */
  visibility: (visible: boolean, userId: string) => Promise<void>;
}

/**
 * Пора ли запирать после возвращения.
 *
 * Отдельной функцией, потому что это единственное здесь правило, которое легко
 * сломать незаметно: ошибка в единице измерения обернётся замком, который либо
 * не запирается никогда, либо спрашивает код после каждого переключения между
 * приложениями.
 */
export function shouldLockAfterAway(awayMs: number, timeoutMin: number): boolean {
  return awayMs >= timeoutMin * 60_000;
}

export const useLockStore = create<LockState>((set, get) => ({
  locked: null,
  attempts: 0,
  awaySince: null,

  async init(userId) {
    const settings = await readLockSettings(userId);
    const digest = await readPinDigest(userId);

    // Замок без отпечатка ПИНа — запертая дверь без ключа. Такого состояния не
    // должно возникать, но если оно возникло, человек не должен остаться
    // снаружи собственных данных.
    set({ locked: settings.enabled && digest !== null, attempts: 0 });
  },

  async unlock(userId, pin) {
    const digest = await readPinDigest(userId);
    if (!digest) {
      set({ locked: false });
      return true;
    }

    if (!(await verifyPin(pin, digest))) {
      set((state) => ({ attempts: state.attempts + 1 }));
      return false;
    }

    set({ locked: false, attempts: 0 });
    return true;
  },

  open() {
    set({ locked: false, attempts: 0 });
  },

  lock() {
    set({ locked: true, attempts: 0 });
  },

  async visibility(visible, userId) {
    if (!visible) {
      set({ awaySince: Date.now() });
      return;
    }

    const { awaySince, locked } = get();
    if (locked !== false || awaySince === null) return;

    const settings = await readLockSettings(userId);
    if (!settings.enabled) return;

    if (shouldLockAfterAway(Date.now() - awaySince, settings.timeoutMin)) {
      set({ locked: true, attempts: 0, awaySince: null });
    }
  },
}));
